import { useState, useEffect } from "react"
import { useParams } from "react-router-dom"

import api from "@api"
import { PROJECT_TYPES } from "@components/Domain/Constants"
import { processInvestments } from "@components/Domain/InvestmentForm/InvestmentForm"

import useInvestmentsStore from "./useInvestmentsStore"

const readProjectOperation = api.get("backstage/ReadPublicPublishedProject")
const indexInvestmentsOperation = api.get("investments/IndexMyInvestments")
export const readAccountOperation = api.get("investments/ReadMyAccount")

const UNEXPECTED_ERROR_TITLE = "Unexpected Error"
export const PROJECT_NOT_FOUND_ERROR_TITLE = "Project Not Found"
export const PROJECT_NOT_FOUND_ERROR_DESCRIPTION = `URL of the project is not
valid or project is not available for investments.`

const SORT_ASC = "asc"
const DOCUMENT_NOT_FOUND_ERROR = "DocumentNotFoundError"


const useProjectInvestmentsStore = request => {
  const { projectId } = useParams()

  const [ error, setError ] = useState()
  const [ project, setProject ] = useState()
  const [ profiles, setProfiles ] = useState()

  const {
    investments,
    addInvestment,
    updateInvestment,
    deleteInvestment,
    setProcessedInvestments,
  } = useInvestmentsStore(request, false)

  let isInProgress = false
  useEffect(() => {
    if (isInProgress) { return }
    isInProgress = true

    request(readProjectOperation.id, { id: projectId }, [ DOCUMENT_NOT_FOUND_ERROR ])
      .then(({ data }) => {
        setProject(data)

        const isFund = data.type === PROJECT_TYPES.FUND

        return isFund
      })
      .catch(exception => {
        const { isExpected } = exception

        if (!isExpected) {
          return setError({
            title: UNEXPECTED_ERROR_TITLE,
            message: exception.message
          })
        }

        setError({
          title: PROJECT_NOT_FOUND_ERROR_TITLE,
          message: PROJECT_NOT_FOUND_ERROR_DESCRIPTION,
        })
      })
      .then((isFund) => Promise.all([
        indexInvestments(isFund),
        indexProfiles()
      ]))
      .catch(exception => {
        return setError({
          title: UNEXPECTED_ERROR_TITLE,
          message: exception.message
        })
      })
  }, [ projectId ])

  const setInvestments = data => {
    const processedInvestments = processInvestments(data)
    setProcessedInvestments(processedInvestments)
  }

  const indexProfiles = () =>
    request(readAccountOperation.id, {})
      .then(({ data }) => setProfiles(data.profiles))

  const indexProjectInvestments = (isFund) =>
    request(indexInvestmentsOperation.id, { projectId, sort: SORT_ASC })
      .then(({ data }) => {
        const pendingInvestments = !isFund
          ? data
          : data
              .filter(({ isFunded, isSigned }) => !isFunded || !isSigned)

        setInvestments(pendingInvestments)
      })

  const indexInvestments =
    project?.type === PROJECT_TYPES.FUND
      ? () => indexProjectInvestments(true)
      : indexProjectInvestments

  const getInvestorSsnNumber = () => {
    const entities = [...(profiles || []), ...(investments || [])]

    for (const entity of entities) {
      const { profileIndividual, profileJoint } = entity

      if (profileIndividual) {
        return profileIndividual.ssnNumber
      }

      if (profileJoint) {
        return profileJoint.ssnNumber
      }
    }
  }

  const getInvestment = investmentId => {
    const investment = investments.find(({ id }) => id === investmentId)

    return investment
  }

  return {
    error,
    project,
    profiles,
    investments,
    addInvestment,
    updateInvestment,
    deleteInvestment,
    indexInvestments,
    getInvestorSsnNumber,
    getInvestment
  }
}

export default useProjectInvestmentsStore
